/**
 * Impuls order helpers.
 * - [data-impuls-order] buttons open the order form: they click the COD app button
 *   (selector from the sticky bar setting), or fall back to submitting the main add-to-cart form.
 * - <impuls-sticky> shows its bar once the main buy area has scrolled out of view.
 */

/** @returns {HTMLFormElement | null} */
function findMainCartForm() {
  return document.querySelector('.shopify-section[id$="__main"] form[action$="/cart/add"], form[action$="/cart/add"]');
}

/** @returns {string} */
function orderSelector() {
  const sticky = document.querySelector('impuls-sticky');
  return sticky?.getAttribute('data-order-selector')?.trim() ?? '';
}

/** @param {Element} el */
function scrollToElement(el) {
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
}

function openOrderForm() {
  const selector = orderSelector();
  if (selector) {
    let appButton = null;
    try {
      appButton = document.querySelector(selector);
    } catch {
      appButton = null;
    }
    if (appButton instanceof HTMLElement) {
      appButton.click();
      return;
    }
  }

  const form = findMainCartForm();
  const submit = form?.querySelector('[type="submit"]:not([disabled])');
  if (submit instanceof HTMLElement) {
    submit.click();
    return;
  }

  if (form) scrollToElement(form);
}

if (!window.__impulsOrderBound) {
  window.__impulsOrderBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-impuls-order]') : null;
    if (!target || target.hasAttribute('disabled')) return;
    event.preventDefault();
    openOrderForm();
  });
}

/**
 * The COD app (EasySell) injects its button with inline !important colours.
 * Re-apply the store's green style and Romanian label whenever the app (re)renders it.
 */
const ES_GREEN = '#1f8f4e';
const MAIN_PRODUCT = '.shopify-section[id$="__main"]';

function styleCodButtons() {
  const productLabel = document.querySelector('[data-es-label]')?.getAttribute('data-es-label')?.trim() ?? '';
  const cardLabel = document.querySelector('[data-es-label-card]')?.getAttribute('data-es-label-card')?.trim() ?? '';
  const cartLabel = document.querySelector('[data-es-label-cart]')?.getAttribute('data-es-label-cart')?.trim() ?? '';

  const isProductPage = document.getElementById('MainContent')?.dataset.template?.startsWith('product') ?? false;

  for (const btn of document.querySelectorAll('.es-popup-button')) {
    if (!(btn instanceof HTMLElement)) continue;
    const inCart = btn.closest('.cart__ctas, cart-drawer, .cart-drawer, .main-cart') !== null;
    const inMainProduct = isProductPage && btn.closest(MAIN_PRODUCT) !== null;
    const label = inCart ? cartLabel : inMainProduct ? productLabel : cardLabel;
    btn.classList.add('impuls-es-btn');
    btn.classList.toggle('impuls-es-btn--main', inMainProduct);
    if (btn.style.getPropertyValue('background') !== ES_GREEN) {
      btn.style.setProperty('background', ES_GREEN, 'important');
      btn.style.setProperty('border-color', ES_GREEN, 'important');
      btn.style.setProperty('border-radius', '10px', 'important');
    }
    const title = btn.querySelector('.es-button-title');
    if (label && title && title.textContent !== label) title.textContent = label;
  }
}

if (!window.__impulsCodObserver) {
  styleCodButtons();
  let queued = false;
  window.__impulsCodObserver = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      styleCodButtons();
    });
  });
  window.__impulsCodObserver.observe(document.body, { childList: true, subtree: true });
}

class ImpulsSticky extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const bar = this.querySelector('.impuls-sticky__bar');
    const anchor = findMainCartForm();
    if (!(bar instanceof HTMLElement)) return;

    if (!anchor) {
      bar.dataset.visible = 'true';
      return;
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const above = entry.boundingClientRect.top < 0;
      bar.dataset.visible = String(!entry.isIntersecting && above);
    });
    this.#observer.observe(anchor);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('impuls-sticky')) {
  customElements.define('impuls-sticky', ImpulsSticky);
}
